import React, { useState, useEffect, useCallback } from "react"
import { FaPencilAlt, FaTrash, FaUserPlus, FaEnvelope, FaTimes } from "react-icons/fa"
import { useAuth } from "./AuthContext"
import { ConfirmationModal } from "./Modals"
import { USER_TYPES, STUDIO_ROLES } from "../firebase"
import {
  createStudio,
  updateStudio,
  deleteStudio,
  getUserStudios,
  getStudioMembers,
  removeStudioMember,
  updateStudioMemberRole,
  findUserByEmail,
  createStudioInvite,
  getStudioInvites,
  revokeStudioInvite,
} from "../utils/firebaseUtils"

export function StudioManager({ onClose }) {
  const { currentUser, userProfile, sendInviteLink } = useAuth()
  const [activeTab, setActiveTab] = useState("my-studios")
  const [studios, setStudios] = useState([])
  const [selectedStudio, setSelectedStudio] = useState(null)
  const [studioMembers, setStudioMembers] = useState([])
  const [studioInvites, setStudioInvites] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Create Studio Form
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newStudioName, setNewStudioName] = useState("")
  const [newStudioDescription, setNewStudioDescription] = useState("")

  // Invite Member Form
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteRole, setInviteRole] = useState(STUDIO_ROLES.MEMBER)

  // Edit Studio
  const [editingStudioId, setEditingStudioId] = useState(null)
  const [editStudioName, setEditStudioName] = useState("")
  const [editStudioDescription, setEditStudioDescription] = useState("")

  // Delete confirmation
  const [confirmDeleteStudioId, setConfirmDeleteStudioId] = useState(null)

  // Remove member confirmation
  const [pendingRemoveMember, setPendingRemoveMember] = useState(null)

  const showMessage = (msg, type = "success") => {
    if (type === "error") {
      setError(msg)
      setSuccess("")
    } else {
      setSuccess(msg)
      setError("")
    }
    setTimeout(() => { setError(""); setSuccess("") }, 4000)
  }

  const loadUserStudios = useCallback(async () => {
    try {
      setLoading(true)
      const userStudios = await getUserStudios(currentUser.uid)
      setStudios(userStudios)
    } catch (err) {
      showMessage("Failed to load studios", "error")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [currentUser])

  useEffect(() => {
    if (currentUser) loadUserStudios()
  }, [currentUser, loadUserStudios])

  useEffect(() => {
    if (activeTab === "members" && selectedStudio && !canManageStudio(selectedStudio)) {
      setActiveTab("my-studios")
    }
  }, [activeTab, selectedStudio])

  const loadStudioMembers = async studioId => {
    try {
      const members = await getStudioMembers(studioId)
      setStudioMembers(members)
    } catch (err) {
      showMessage("Failed to load studio members", "error")
      console.error(err)
    }
  }

  const loadStudioInvites = async studioId => {
    try {
      const invites = await getStudioInvites(studioId)
      setStudioInvites(invites)
    } catch (err) {
      console.error("Error loading invites:", err)
    }
  }

  const handleCreateStudio = async e => {
    e.preventDefault()
    if (!newStudioName.trim()) return

    try {
      setLoading(true)
      await createStudio({ name: newStudioName.trim(), description: newStudioDescription.trim() }, currentUser.uid)
      setNewStudioName("")
      setNewStudioDescription("")
      setShowCreateForm(false)
      await loadUserStudios()
      showMessage("Studio created successfully!")
    } catch (err) {
      showMessage("Failed to create studio", "error")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteStudio = async studioId => {
    try {
      setLoading(true)
      await deleteStudio(studioId, currentUser.uid)
      setConfirmDeleteStudioId(null)
      setSelectedStudio(null)
      setActiveTab("my-studios")
      await loadUserStudios()
      showMessage("Studio deleted successfully")
    } catch (err) {
      showMessage(err.message || "Failed to delete studio", "error")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Always sends a magic link email. If the user already has an account,
   * they'll be added to the studio when they click the link. If not,
   * they'll create an account and join in one step.
   */
  const handleInviteMember = async e => {
    e.preventDefault()
    if (!inviteEmail.trim() || !selectedStudio) return

    try {
      setLoading(true)
      setError("")

      const existingUser = await findUserByEmail(inviteEmail.trim())

      if (existingUser) {
        // Check if already a member before sending
        const isMember = studioMembers.some(m => m.email === inviteEmail.trim().toLowerCase() || m.id === existingUser.id)
        if (isMember) {
          showMessage("This person is already a member of the studio", "error")
          setLoading(false)
          return
        }
      }

      const result = await createStudioInvite(selectedStudio.id, currentUser.uid, {
        email: inviteEmail.trim(),
        role: inviteRole,
      })

      await sendInviteLink(inviteEmail.trim(), result.inviteCode)
      await loadStudioInvites(selectedStudio.id)
      showMessage(`Invitation email sent to ${inviteEmail}!`)

      setInviteEmail("")
      setInviteRole(STUDIO_ROLES.MEMBER)
      setShowInviteForm(false)
    } catch (err) {
      showMessage("Failed to invite member: " + (err.message || "Unknown error"), "error")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeInvite = async inviteId => {
    try {
      await revokeStudioInvite(inviteId)
      await loadStudioInvites(selectedStudio.id)
      showMessage("Invitation cancelled")
    } catch (err) {
      showMessage("Failed to cancel invitation", "error")
      console.error(err)
    }
  }

  const handleRemoveMember = (memberId, userId) => {
    setPendingRemoveMember({ memberId, userId })
  }

  const confirmRemoveMember = async () => {
    if (!pendingRemoveMember) return
    const { userId } = pendingRemoveMember
    setPendingRemoveMember(null)

    try {
      await removeStudioMember(selectedStudio.id, userId)
      await loadStudioMembers(selectedStudio.id)
      showMessage("Member removed")
    } catch (err) {
      showMessage("Failed to remove member", "error")
      console.error(err)
    }
  }

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateStudioMemberRole(selectedStudio.id, userId, newRole)
      await loadStudioMembers(selectedStudio.id)
      showMessage("Role updated")
    } catch (err) {
      showMessage("Failed to update member role", "error")
      console.error(err)
    }
  }

  const handleEditStudio = studio => {
    setEditingStudioId(studio.id)
    setEditStudioName(studio.name)
    setEditStudioDescription(studio.description || "")
  }

  const handleCancelEdit = () => {
    setEditingStudioId(null)
    setEditStudioName("")
    setEditStudioDescription("")
  }

  const handleSaveStudio = async studioId => {
    if (!editStudioName.trim()) {
      showMessage("Studio name cannot be empty", "error")
      return
    }

    try {
      setLoading(true)
      await updateStudio(studioId, { name: editStudioName.trim(), description: editStudioDescription.trim() })
      setEditingStudioId(null)
      setEditStudioName("")
      setEditStudioDescription("")
      await loadUserStudios()
      showMessage("Studio updated successfully")
    } catch (err) {
      showMessage("Failed to update studio", "error")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const canManageStudio = studio => {
    return studio.userRole === STUDIO_ROLES.OWNER || studio.userRole === STUDIO_ROLES.ADMIN
  }

  const isStudioOwner = studio => studio.userRole === STUDIO_ROLES.OWNER
  const canCreateStudio = userProfile?.userType === USER_TYPES.STUDIO_ADMIN

  const selectStudioForManagement = studio => {
    setSelectedStudio(studio)
    setActiveTab("members")
    loadStudioMembers(studio.id)
    loadStudioInvites(studio.id)
  }

  const getRoleBadgeColor = role => {
    switch (role) {
      case STUDIO_ROLES.OWNER:
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case STUDIO_ROLES.ADMIN:
        return "bg-blue-100 text-blue-800 border-blue-200"
      case STUDIO_ROLES.MEMBER:
        return "bg-green-100 text-green-800 border-green-200"
      case STUDIO_ROLES.VIEWER:
        return "bg-gray-100 text-gray-800 border-gray-200"
      default:
        return "bg-gray-100 text-gray-600 border-gray-200"
    }
  }

  const pendingInvites = studioInvites.filter(i => !i.isExpired)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-purple-50 to-blue-50 rounded-t-xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {userProfile?.userType === USER_TYPES.STUDIO_ADMIN ? "Studio Management" : "My Studios"}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Manage your studios and members</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
            aria-label="Close studio manager"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Alerts */}
        {(error || success) && (
          <div className={`mx-5 mt-4 px-4 py-3 rounded-lg text-sm flex items-center justify-between ${error ? "bg-red-50 border border-red-200 text-red-700" : "bg-green-50 border border-green-200 text-green-700"}`}>
            <span>{error || success}</span>
            <button onClick={() => { setError(""); setSuccess("") }} className="ml-3 font-bold hover:opacity-70">×</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b bg-gray-50 px-2">
          <button
            onClick={() => setActiveTab("my-studios")}
            className={`px-5 py-3 font-medium text-sm transition-colors relative ${
              activeTab === "my-studios"
                ? "text-purple-700 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-purple-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Studios
          </button>
          {selectedStudio && canManageStudio(selectedStudio) && (
            <>
              <button
                onClick={() => setActiveTab("members")}
                className={`px-5 py-3 font-medium text-sm transition-colors relative ${
                  activeTab === "members"
                    ? "text-purple-700 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-purple-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Members
                <span className="ml-1.5 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">
                  {studioMembers.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab("settings")}
                className={`px-5 py-3 font-medium text-sm transition-colors relative ${
                  activeTab === "settings"
                    ? "text-purple-700 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-purple-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Settings
              </button>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* ======================== MY STUDIOS TAB ======================== */}
          {activeTab === "my-studios" && (
            <div>
              {canCreateStudio && (
                <div className="mb-6">
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2.5 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Studio
                  </button>
                </div>
              )}

              {/* Create Studio Form */}
              {showCreateForm && (
                <div className="mb-6 p-5 border border-purple-200 rounded-xl bg-purple-50">
                  <h3 className="text-lg font-semibold mb-4 text-purple-900">Create New Studio</h3>
                  <form onSubmit={handleCreateStudio}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Studio Name</label>
                      <input
                        type="text"
                        value={newStudioName}
                        onChange={e => setNewStudioName(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="e.g., Dance Academy Studio"
                        required
                        autoFocus
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                      <textarea
                        value={newStudioDescription}
                        onChange={e => setNewStudioDescription(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        placeholder="Brief description of your studio..."
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" disabled={loading} className="bg-purple-600 text-white px-5 py-2.5 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
                        {loading ? "Creating..." : "Create Studio"}
                      </button>
                      <button type="button" onClick={() => setShowCreateForm(false)} className="bg-white text-gray-600 px-5 py-2.5 rounded-lg hover:bg-gray-100 text-sm border border-gray-200">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Studios List */}
              <div className="space-y-3">
                {loading && studios.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="animate-spin h-8 w-8 border-4 border-purple-200 border-t-purple-600 rounded-full mx-auto mb-3"></div>
                    <p className="text-gray-500 text-sm">Loading studios...</p>
                  </div>
                ) : studios.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <p className="text-gray-500 font-medium">No studios yet</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {canCreateStudio ? "Create your first studio to get started!" : "You haven't been invited to any studios yet."}
                    </p>
                  </div>
                ) : (
                  studios.map(studio => (
                    <div key={studio.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all bg-white">
                      {editingStudioId === studio.id ? (
                        <div>
                          <div className="mb-3">
                            <input
                              type="text"
                              value={editStudioName}
                              onChange={e => setEditStudioName(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm font-medium"
                            />
                          </div>
                          <div className="mb-3">
                            <textarea
                              value={editStudioDescription}
                              onChange={e => setEditStudioDescription(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                              rows={2}
                            />
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleSaveStudio(studio.id)} disabled={loading} className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm">
                              Save
                            </button>
                            <button onClick={handleCancelEdit} className="bg-gray-100 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm">
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-lg font-semibold text-gray-900 truncate">{studio.name}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getRoleBadgeColor(studio.userRole)}`}>
                                {studio.userRole}
                              </span>
                            </div>
                            {studio.description && <p className="text-sm text-gray-500 mb-1 line-clamp-2">{studio.description}</p>}
                            <p className="text-xs text-gray-400">
                              Joined {studio.joinedAt?.toDate?.()?.toLocaleDateString() || "recently"}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 ml-4">
                            {canManageStudio(studio) && (
                              <>
                                <button
                                  onClick={() => handleEditStudio(studio)}
                                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Edit studio"
                                >
                                  <FaPencilAlt className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => selectStudioForManagement(studio)}
                                  className="flex items-center gap-1.5 bg-purple-50 text-purple-700 px-3 py-2 rounded-lg hover:bg-purple-100 transition-colors text-sm font-medium"
                                >
                                  <FaUserPlus className="w-3.5 h-3.5" />
                                  Manage
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ======================== MEMBERS TAB ======================== */}
          {activeTab === "members" && selectedStudio && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedStudio.name} — Members</h3>
                  <p className="text-sm text-gray-500">{studioMembers.length} member{studioMembers.length !== 1 ? "s" : ""}</p>
                </div>
                {canManageStudio(selectedStudio) && (
                  <button
                    onClick={() => setShowInviteForm(true)}
                    className="flex items-center gap-1.5 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    <FaUserPlus className="w-3.5 h-3.5" />
                    Invite Member
                  </button>
                )}
              </div>

              {/* Invite Member Form */}
              {showInviteForm && (
                <div className="mb-5 p-5 border border-purple-200 rounded-xl bg-purple-50">
                  <h4 className="font-semibold mb-2 text-purple-900">Invite a Member</h4>
                  <p className="text-sm text-purple-700 mb-4">
                    Enter their email address. If they already have an account, they'll be added instantly.
                    Otherwise, they'll receive an email invitation to join.
                  </p>
                  <form onSubmit={handleInviteMember}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                      <div className="sm:col-span-2">
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                          placeholder="Enter email address"
                          required
                          autoFocus
                        />
                      </div>
                      <select
                        value={inviteRole}
                        onChange={e => setInviteRole(e.target.value)}
                        className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      >
                        <option value={STUDIO_ROLES.MEMBER}>Member</option>
                        <option value={STUDIO_ROLES.ADMIN}>Admin</option>
                        <option value={STUDIO_ROLES.VIEWER}>Viewer</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button type="submit" disabled={loading} className="bg-purple-600 text-white px-5 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium">
                        {loading ? "Sending..." : "Send Invitation"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowInviteForm(false); setInviteEmail(""); setInviteRole(STUDIO_ROLES.MEMBER) }}
                        className="bg-white text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100 text-sm border border-gray-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Members List */}
              <div className="space-y-2">
                {studioMembers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">No members found.</div>
                ) : (
                  studioMembers.map(member => (
                    <div key={member.id} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between bg-white hover:shadow-sm transition-shadow">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {(member.displayName || member.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900 text-sm">{member.displayName || "Unknown"}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getRoleBadgeColor(member.role)}`}>
                              {member.role}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canManageStudio(selectedStudio) && member.role !== STUDIO_ROLES.OWNER ? (
                          <>
                            <select
                              value={member.role}
                              onChange={e => handleRoleChange(member.id, e.target.value)}
                              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value={STUDIO_ROLES.VIEWER}>Viewer</option>
                              <option value={STUDIO_ROLES.MEMBER}>Member</option>
                              <option value={STUDIO_ROLES.ADMIN}>Admin</option>
                            </select>
                            <button
                              onClick={() => handleRemoveMember(member.membershipId, member.id)}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title={`Remove ${member.displayName || "member"}`}
                            >
                              <FaTrash className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pending Invitations */}
              {pendingInvites.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 text-sm mb-3">Pending Invitations</h4>
                  <div className="space-y-2">
                    {pendingInvites.map(invite => (
                      <div key={invite.id} className="border border-gray-200 rounded-xl p-4 flex items-center justify-between bg-gray-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <FaEnvelope className="w-4 h-4 text-orange-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">{invite.invitedEmail}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getRoleBadgeColor(invite.role)}`}>
                                {invite.role}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              Invited · Expires {invite.expiresAt?.toDate ? invite.expiresAt.toDate().toLocaleDateString() : new Date(invite.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRevokeInvite(invite.id)}
                          className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ======================== SETTINGS TAB ======================== */}
          {activeTab === "settings" && selectedStudio && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-5">{selectedStudio.name} — Settings</h3>

              <div className="mb-6 p-5 border border-gray-200 rounded-xl bg-white">
                <h4 className="font-medium text-gray-900 mb-3">Studio Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Name</span>
                    <span className="font-medium">{selectedStudio.name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Your Role</span>
                    <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${getRoleBadgeColor(selectedStudio.userRole)}`}>
                      {selectedStudio.userRole}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-500">Members</span>
                    <span className="font-medium">{studioMembers.length}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Pending Invites</span>
                    <span className="font-medium">{pendingInvites.length}</span>
                  </div>
                </div>
              </div>

              {isStudioOwner(selectedStudio) && (
                <div className="p-5 border-2 border-red-200 rounded-xl bg-red-50">
                  <h4 className="font-medium text-red-900 mb-2">Danger Zone</h4>
                  <p className="text-sm text-red-700 mb-4">
                    Deleting a studio will permanently remove all memberships and invitations.
                    Studio designs will remain with their creators.
                  </p>
                  {confirmDeleteStudioId === selectedStudio.id ? (
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-medium text-red-800">Are you sure? This cannot be undone.</p>
                      <button
                        onClick={() => handleDeleteStudio(selectedStudio.id)}
                        disabled={loading}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                      >
                        {loading ? "Deleting..." : "Yes, Delete Studio"}
                      </button>
                      <button onClick={() => setConfirmDeleteStudioId(null)} className="bg-white text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100 text-sm border border-gray-200">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteStudioId(selectedStudio.id)}
                      className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm font-medium"
                    >
                      <FaTrash className="w-3.5 h-3.5" />
                      Delete Studio
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Remove Member Confirmation Modal */}
      {pendingRemoveMember && (
        <ConfirmationModal
          title="Remove Member"
          message="Are you sure you want to remove this member from the studio?"
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={confirmRemoveMember}
          onCancel={() => setPendingRemoveMember(null)}
          confirmButtonClass="bg-red-600 hover:bg-red-700"
        />
      )}
    </div>
  )
}
